/**
 * The `.sei` corpus transfer format.
 *
 * A `.sei` file is an ordinary ZIP archive. Renaming it to `.zip` opens it in
 * any archive tool; `README.txt` inside explains the layout, and the writing
 * itself is duplicated as plain Markdown under `readable/`.
 *
 *   mimetype                    "application/vnd.superelectric-ink.sei+zip" (stored, first entry)
 *   manifest.json               format version, app info, contents summary, SHA-256 of every file
 *   README.txt
 *   corpus/tree.json            desks, folders, pages and notes, plus the Trash
 *   corpus/pages/<id>.json|md   exact page content, as the app stores it
 *   corpus/history/<id>/…       version history (optional)
 *   settings/settings.json      app settings
 *   settings/local-backup.json  local backup preferences
 *   settings/interface.json     open desk, pinned panel, search view
 *   fonts/families.json         installed catalog fonts (optional)
 *   fonts/files/<path>          their font files
 *   readable/…                  Markdown copies, arranged as in the Corpus Panel
 *
 * A password-protected file is also a ZIP, but holds only `mimetype`,
 * `README.txt`, a `manifest.json` describing the encryption, and
 * `payload.encrypted`: the complete archive above, encrypted with AES-256-GCM
 * under a key derived from the password with Argon2id.
 */
import { strFromU8, strToU8, unzipSync, zipSync, type Zippable } from 'fflate'
import { argon2id } from 'hash-wasm'
import { fromBase64Url, toBase64Url } from '../crypto/crypto'
import type { CorpusNode, TrashEntry } from '../../stores/corpus'
import type { FontCatalogFamily } from '../../types/fontCatalog'

const SEI_MIME_TYPE = 'application/vnd.superelectric-ink.sei+zip'
export const SEI_FORMAT = 'superelectric-ink-corpus'
export const SEI_FORMAT_VERSION = 1

export const SEI_PATHS = {
  mimetype: 'mimetype',
  manifest: 'manifest.json',
  readme: 'README.txt',
  tree: 'corpus/tree.json',
  pages: 'corpus/pages/',
  history: 'corpus/history/',
  settings: 'settings/settings.json',
  localBackup: 'settings/local-backup.json',
  interfaceState: 'settings/interface.json',
  fontFamilies: 'fonts/families.json',
  fontFiles: 'fonts/files/',
  readable: 'readable/',
  encryptedPayload: 'payload.encrypted',
} as const

/** Settings that describe this machine rather than the writer, or are secrets. */
export const SEI_OMITTED_SETTINGS = ['markdownMirrorPath', 'cloudSyncApiKey'] as const

/** Refuse archives that would expand beyond this, whatever their manifest says. */
const MAX_UNCOMPRESSED_BYTES = 4 * 1024 * 1024 * 1024

const ARGON2_PARAMS = {
  memorySize: 65536, // KiB (64 MB)
  iterations: 3,
  parallelism: 4,
}
const ENCRYPTION_AAD = strToU8(`${SEI_FORMAT}/${SEI_FORMAT_VERSION}`)

export interface SeiContentCounts {
  desks: number
  folders: number
  pages: number
  notes: number
  trashed: number
  snapshots: number
  fonts: number
}

interface SeiEncryptionParams {
  cipher: 'AES-256-GCM'
  kdf: 'argon2id'
  salt: string
  iv: string
  memorySize: number
  iterations: number
  parallelism: number
}

export interface SeiManifest {
  format: typeof SEI_FORMAT
  formatVersion: number
  encrypted: boolean
  createdAt: string
  app: { name: string; version: string; platform: string }
  /** Unencrypted archives only. */
  contents?: SeiContentCounts
  includes?: { settings: boolean; history: boolean; fonts: boolean; readable: boolean }
  /** SHA-256 (hex) of every entry except `mimetype` and `manifest.json`. */
  files?: Record<string, string>
  /** Encrypted archives only. */
  encryption?: SeiEncryptionParams
}

export interface SeiTree {
  version: number
  activePageId: string | null
  tree: CorpusNode[]
  trash: TrashEntry[]
}

export interface SeiPage {
  id: string
  content: string
}

export interface SeiHistoryIndexEntry {
  file: string
  timestamp: number
  wordCount: number
  label: string
}

interface SeiSnapshot {
  timestamp: number
  wordCount: number
  label: string
  content: string
}

export interface SeiSettingsFile {
  values: Record<string, any>
  omitted: string[]
}

export interface SeiInterfaceState {
  focusStack: string[]
  pinned: boolean
  searchView: 'list' | 'tree'
}

interface SeiFont {
  family: FontCatalogFamily
  files: Array<{ path: string; bytes: Uint8Array }>
}

/** Everything a `.sei` file holds, validated and ready to import. */
export interface SeiContents {
  manifest: SeiManifest
  tree: SeiTree
  pages: Map<string, SeiPage>
  history: Map<string, SeiSnapshot[]>
  settings: SeiSettingsFile | null
  localBackup: Record<string, any> | null
  interfaceState: SeiInterfaceState | null
  fonts: SeiFont[]
}

class SeiFormatError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SeiFormatError'
  }
}

export class SeiPasswordError extends Error {
  constructor() {
    super('That password does not unlock this file.')
    this.name = 'SeiPasswordError'
  }
}

// ── Tree helpers ─────────────────────────────────────────────────────────────

export function flattenCorpusNodes(
  nodes: CorpusNode[],
  parentId: string | null = null,
): Array<{ node: CorpusNode; parentId: string | null }> {
  const flattened: Array<{ node: CorpusNode; parentId: string | null }> = []
  for (const node of nodes) {
    flattened.push({ node, parentId })
    if (node.children?.length) flattened.push(...flattenCorpusNodes(node.children, node.id))
  }
  return flattened
}

/** Pages whose parent is a page are notes. */
export function countCorpus(tree: CorpusNode[], trash: TrashEntry[]): Omit<SeiContentCounts, 'snapshots' | 'fonts'> {
  const counts = { desks: 0, folders: 0, pages: 0, notes: 0, trashed: 0 }
  const visit = (nodes: CorpusNode[], parent: CorpusNode | null) => {
    for (const node of nodes) {
      if (node.subtype === 'desk') counts.desks++
      else if (node.subtype === 'folder') counts.folders++
      else if (parent?.subtype === 'page') counts.notes++
      else counts.pages++
      if (node.children?.length) visit(node.children, node)
    }
  }
  visit(tree, null)
  for (const entry of trash) counts.trashed += flattenCorpusNodes([entry.node]).length
  return counts
}

// ── ID remapping ─────────────────────────────────────────────────────────────

/**
 * Replaces whole-token occurrences of old IDs. Corpus IDs only ever contain
 * `[A-Za-z0-9_-]`, so any reference to one (a mention, a setting keyed by a
 * folder, a link) is a complete token of that alphabet.
 */
export function remapIdsInString(value: string, idMap: Map<string, string>): string {
  if (idMap.size === 0) return value
  return value.replace(/[A-Za-z0-9_-]+/g, (token) => idMap.get(token) ?? token)
}

/** Deep copy with IDs remapped in every string value and object key. */
export function remapIdsDeep<T>(value: T, idMap: Map<string, string>): T {
  if (typeof value === 'string') return remapIdsInString(value, idMap) as T
  if (Array.isArray(value)) return value.map((item) => remapIdsDeep(item, idMap)) as T
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      result[idMap.get(key) ?? key] = remapIdsDeep(item, idMap)
    }
    return result as T
  }
  return value
}

// ── Bytes ────────────────────────────────────────────────────────────────────

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes as BufferSource))
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

export function jsonBytes(value: unknown): Uint8Array {
  return strToU8(JSON.stringify(value, null, 2))
}

function isZip(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04
}

function zipEntries(entries: Array<[string, Uint8Array]>): Uint8Array {
  // Object keys keep insertion order, so `mimetype` stays the first entry and,
  // being stored uncompressed, readable at a fixed offset (as in EPUB/ODF).
  const zippable: Zippable = {}
  for (const [path, bytes] of entries) {
    zippable[path] = path === SEI_PATHS.mimetype ? [bytes, { level: 0 }] : [bytes, { level: 6 }]
  }
  return zipSync(zippable)
}

function unzipGuarded(bytes: Uint8Array): Record<string, Uint8Array> {
  if (!isZip(bytes)) throw new SeiFormatError('This is not a Superelectric Ink corpus file.')
  let total = 0
  try {
    return unzipSync(bytes, {
      filter: (file) => {
        total += file.originalSize
        if (total > MAX_UNCOMPRESSED_BYTES) {
          throw new SeiFormatError('This file expands to more than 4 GB and was not opened.')
        }
        return !file.name.endsWith('/')
      },
    })
  } catch (error) {
    if (error instanceof SeiFormatError) throw error
    throw new SeiFormatError('This file is damaged and could not be opened.')
  }
}

function readJson<T>(entries: Record<string, Uint8Array>, path: string): T {
  const bytes = entries[path]
  if (!bytes) throw new SeiFormatError(`This file is incomplete: ${path} is missing.`)
  try {
    return JSON.parse(strFromU8(bytes)) as T
  } catch {
    throw new SeiFormatError(`This file is damaged: ${path} is not valid JSON.`)
  }
}

// ── Writing ──────────────────────────────────────────────────────────────────

/**
 * Packs the given entries into a finished archive: computes every hash,
 * writes the manifest and orders `mimetype` first.
 */
export async function packArchive(
  files: Map<string, Uint8Array>,
  manifest: Omit<SeiManifest, 'files' | 'encrypted' | 'encryption'>,
): Promise<Uint8Array> {
  const hashes: Record<string, string> = {}
  for (const [path, bytes] of files) hashes[path] = await sha256Hex(bytes)
  const fullManifest: SeiManifest = { ...manifest, encrypted: false, files: hashes }
  return zipEntries([
    [SEI_PATHS.mimetype, strToU8(SEI_MIME_TYPE)],
    [SEI_PATHS.manifest, jsonBytes(fullManifest)],
    ...files,
  ])
}

async function deriveKey(password: string, salt: Uint8Array, params: typeof ARGON2_PARAMS): Promise<CryptoKey> {
  const raw = await argon2id({
    password: password.normalize('NFC'),
    salt,
    memorySize: params.memorySize,
    iterations: params.iterations,
    parallelism: params.parallelism,
    hashLength: 32,
    outputType: 'binary',
  }) as Uint8Array
  try {
    return await crypto.subtle.importKey('raw', raw as BufferSource, 'AES-GCM', false, ['encrypt', 'decrypt'])
  } finally {
    raw.fill(0)
  }
}

/** Wraps a finished archive in a password-protected outer archive. */
export async function encryptArchive(
  inner: Uint8Array,
  password: string,
  app: SeiManifest['app'],
  readme: string,
): Promise<Uint8Array> {
  const salt = crypto.getRandomValues(new Uint8Array(16))
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const key = await deriveKey(password, salt, ARGON2_PARAMS)
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource, additionalData: ENCRYPTION_AAD as BufferSource },
    key,
    inner as BufferSource,
  ))
  const manifest: SeiManifest = {
    format: SEI_FORMAT,
    formatVersion: SEI_FORMAT_VERSION,
    encrypted: true,
    createdAt: new Date().toISOString(),
    app,
    encryption: {
      cipher: 'AES-256-GCM',
      kdf: 'argon2id',
      salt: toBase64Url(salt),
      iv: toBase64Url(iv),
      ...ARGON2_PARAMS,
    },
  }
  return zipEntries([
    [SEI_PATHS.mimetype, strToU8(SEI_MIME_TYPE)],
    [SEI_PATHS.manifest, jsonBytes(manifest)],
    [SEI_PATHS.readme, strToU8(readme)],
    [SEI_PATHS.encryptedPayload, ciphertext],
  ])
}

// ── Reading ──────────────────────────────────────────────────────────────────

export type OpenedArchive =
  | { kind: 'locked'; manifest: SeiManifest; payload: Uint8Array }
  | { kind: 'ready'; contents: SeiContents }

function checkManifest(manifest: SeiManifest): void {
  if (!manifest || manifest.format !== SEI_FORMAT) {
    throw new SeiFormatError('This is not a Superelectric Ink corpus file.')
  }
  if (!Number.isInteger(manifest.formatVersion) || manifest.formatVersion < 1) {
    throw new SeiFormatError('This file has an unrecognized format version.')
  }
  if (manifest.formatVersion > SEI_FORMAT_VERSION) {
    throw new SeiFormatError(
      'This file was made by a newer version of Superelectric Ink. Update the app, then import it again.',
    )
  }
}

/** Opens a `.sei` file. Encrypted files come back locked until `unlockArchive`. */
export async function openArchive(bytes: Uint8Array): Promise<OpenedArchive> {
  const entries = unzipGuarded(bytes)
  const mimetype = entries[SEI_PATHS.mimetype]
  if (!mimetype || strFromU8(mimetype).trim() !== SEI_MIME_TYPE) {
    throw new SeiFormatError('This is not a Superelectric Ink corpus file.')
  }
  const manifest = readJson<SeiManifest>(entries, SEI_PATHS.manifest)
  checkManifest(manifest)

  if (manifest.encrypted) {
    const payload = entries[SEI_PATHS.encryptedPayload]
    if (!payload || !manifest.encryption) throw new SeiFormatError('This file is incomplete: its encrypted contents are missing.')
    return { kind: 'locked', manifest, payload }
  }
  return { kind: 'ready', contents: await parseContents(entries, manifest) }
}

export async function unlockArchive(locked: { manifest: SeiManifest; payload: Uint8Array }, password: string): Promise<SeiContents> {
  const params = locked.manifest.encryption!
  if (params.cipher !== 'AES-256-GCM' || params.kdf !== 'argon2id') {
    throw new SeiFormatError('This file uses an encryption method this version of the app does not support.')
  }
  const key = await deriveKey(password, fromBase64Url(params.salt), {
    memorySize: params.memorySize,
    iterations: params.iterations,
    parallelism: params.parallelism,
  })
  let inner: Uint8Array
  try {
    inner = new Uint8Array(await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromBase64Url(params.iv) as BufferSource, additionalData: ENCRYPTION_AAD as BufferSource },
      key,
      locked.payload as BufferSource,
    ))
  } catch {
    throw new SeiPasswordError()
  }
  const opened = await openArchive(inner)
  if (opened.kind !== 'ready') throw new SeiFormatError('This file is damaged: its contents are encrypted twice.')
  return opened.contents
}

function validateTree(tree: SeiTree): void {
  if (!tree || !Array.isArray(tree.tree)) throw new SeiFormatError('This file is damaged: the corpus tree is unreadable.')
  if (!Array.isArray(tree.trash)) tree.trash = []
  const seen = new Set<string>()
  const check = (nodes: CorpusNode[]) => {
    for (const node of nodes) {
      if (!node || typeof node.id !== 'string' || !/^[A-Za-z0-9_-]+$/.test(node.id)) {
        throw new SeiFormatError('This file is damaged: an item in the corpus tree has an invalid ID.')
      }
      if (seen.has(node.id)) throw new SeiFormatError('This file is damaged: two items share one ID.')
      if (!['desk', 'folder', 'page'].includes(node.subtype)) {
        throw new SeiFormatError(`This file is damaged: “${node.name}” has an unknown type.`)
      }
      seen.add(node.id)
      if (node.children !== undefined && !Array.isArray(node.children)) {
        throw new SeiFormatError(`This file is damaged: “${node.name}” has unreadable children.`)
      }
      check(node.children || [])
    }
  }
  check(tree.tree)
  for (const entry of tree.trash) {
    if (!entry?.node) throw new SeiFormatError('This file is damaged: a Trash entry is unreadable.')
    check([entry.node])
  }
}

async function parseContents(entries: Record<string, Uint8Array>, manifest: SeiManifest): Promise<SeiContents> {
  // Every listed file must be present and intact.
  for (const [path, expected] of Object.entries(manifest.files || {})) {
    const bytes = entries[path]
    if (!bytes) throw new SeiFormatError(`This file is incomplete: ${path} is missing.`)
    if (await sha256Hex(bytes) !== expected) {
      throw new SeiFormatError(`This file is damaged: ${path} does not match its checksum.`)
    }
  }

  const tree = readJson<SeiTree>(entries, SEI_PATHS.tree)
  validateTree(tree)

  const pages = new Map<string, SeiPage>()
  const history = new Map<string, SeiSnapshot[]>()
  const allNodes = [
    ...flattenCorpusNodes(tree.tree),
    ...tree.trash.flatMap((entry) => flattenCorpusNodes([entry.node])),
  ].map(({ node }) => node)

  for (const node of allNodes) {
    if (node.subtype !== 'page') continue
    const jsonEntry = entries[`${SEI_PATHS.pages}${node.id}.json`]
    if (!jsonEntry) {
      throw new SeiFormatError(`This file is incomplete: the page “${node.name}” has no content.`)
    }
    pages.set(node.id, { id: node.id, content: strFromU8(jsonEntry) })

    const indexPath = `${SEI_PATHS.history}${node.id}/index.json`
    if (entries[indexPath]) {
      const index = readJson<SeiHistoryIndexEntry[]>(entries, indexPath)
      const snapshots: SeiSnapshot[] = []
      for (const item of Array.isArray(index) ? index : []) {
        const bytes = entries[`${SEI_PATHS.history}${node.id}/${item.file}`]
        if (!bytes) continue
        snapshots.push({
          timestamp: Number(item.timestamp) || 0,
          wordCount: Number(item.wordCount) || 0,
          label: typeof item.label === 'string' ? item.label : 'Snapshot',
          content: strFromU8(bytes),
        })
      }
      snapshots.sort((a, b) => a.timestamp - b.timestamp)
      if (snapshots.length) history.set(node.id, snapshots)
    }
  }

  const settings = entries[SEI_PATHS.settings] ? readJson<SeiSettingsFile>(entries, SEI_PATHS.settings) : null
  if (settings && (typeof settings.values !== 'object' || settings.values === null)) {
    throw new SeiFormatError('This file is damaged: its settings are unreadable.')
  }

  const fonts: SeiFont[] = []
  if (entries[SEI_PATHS.fontFamilies]) {
    const families = readJson<FontCatalogFamily[]>(entries, SEI_PATHS.fontFamilies)
    for (const family of Array.isArray(families) ? families : []) {
      if (!family || typeof family.id !== 'string' || !Array.isArray(family.files)) continue
      const files = family.files.flatMap((file) => {
        const bytes = entries[`${SEI_PATHS.fontFiles}${file.path}`]
        return bytes ? [{ path: file.path, bytes }] : []
      })
      fonts.push({ family, files })
    }
  }

  return {
    manifest,
    tree,
    pages,
    history,
    settings,
    localBackup: entries[SEI_PATHS.localBackup] ? readJson<Record<string, any>>(entries, SEI_PATHS.localBackup) : null,
    interfaceState: entries[SEI_PATHS.interfaceState]
      ? readJson<SeiInterfaceState>(entries, SEI_PATHS.interfaceState)
      : null,
    fonts,
  }
}

// ── README ───────────────────────────────────────────────────────────────────

export function buildReadme(encrypted: boolean): string {
  const intro = [
    'SUPERELECTRIC INK CORPUS',
    '========================',
    '',
    'This file holds a complete Superelectric Ink corpus: every desk, folder,',
    'page and note, the Trash, and the app settings. To bring it into',
    'Superelectric Ink, open Settings → Export & Import and choose Import Corpus,',
    'or open this file with the app.',
    '',
  ]
  if (encrypted) {
    return [
      ...intro,
      'This copy is password protected. Its contents are in payload.encrypted,',
      'encrypted with AES-256-GCM. The key is derived from the password with',
      'Argon2id using the salt and cost parameters recorded in manifest.json.',
      `The additional authenticated data is the text "${SEI_FORMAT}/${SEI_FORMAT_VERSION}".`,
      'Once decrypted, the payload is an ordinary, unprotected corpus file',
      'with the layout described in its own README.txt.',
      '',
    ].join('\n')
  }
  return [
    ...intro,
    'A .sei file is an ordinary ZIP archive. You can rename it to .zip and open',
    'it with any archive tool.',
    '',
    'readable/              Your writing as plain Markdown (.md) files, arranged',
    '                       as in the Corpus Panel. Pages with notes have a',
    '                       folder of the same name holding those notes.',
    'corpus/tree.json       The corpus structure (JSON).',
    'corpus/pages/          The exact content of each page (Tiptap/ProseMirror',
    '                       JSON, or Markdown), named by page ID.',
    'corpus/history/        Saved versions of each page, if included.',
    'settings/              App settings (JSON).',
    'fonts/                 Downloaded fonts, if included.',
    'manifest.json          Format version, a summary, and a SHA-256 checksum',
    '                       for every file.',
    '',
  ].join('\n')
}
